/**
 * V244: Unified Profile Photo Hook - Single Source of Truth with PhotoStore
 * 
 * Priority:
 *   1. PhotoStore (stable key: profile_photo:userId) - blob cached (instant)
 *   2. globalDataStore.state.profile.photo_urls[0] (truth)
 *   3. persistentCache (survives reload)
 *   4. PhotoContext cache (includes cache-busted URLs after upload)
 *   5. NetworkDataContext currentUser.photo_urls[0] (for current user)
 *   6. NetworkDataContext userCache (for other users)
 *   7. Default Google image (for current user, if no DB photo)
 * 
 * Usage:
 *   const { photoUrl, updatePhoto } = useProfilePhoto(userId);
 */

import { useCallback, useMemo, useState, useEffect } from 'react';
import { usePhotoContext } from '@/contexts/PhotoContext';
import { useCurrentUser, useUser, useUpdateCurrentUser } from '@/contexts/NetworkDataContext';
import { globalDataStore } from '@/lib/globalDataStore';
import { migrateLegacyProfilePhotoCache, getProfilePhotoCachePayload } from '@/lib/profilePhotoKeys';
import { getPhoto, subscribeToPhotoUpdates } from '@/lib/photoStore';
import { getOverride } from '@/lib/uiPhotoOverride';
import { pickBestPhotoSrc } from '@/lib/pickBestPhotoSrc';

interface UseProfilePhotoResult {
  /** The best available photo URL (cache-busted if recently updated) */
  photoUrl: string | null;
  /** Update photo in both PhotoContext and NetworkDataContext */
  updatePhoto: (newUrl: string) => Promise<void>;
  /** Clear cached photo for this user */
  clearPhoto: () => void;
  /** Whether this is the current user's photo */
  isCurrentUser: boolean;
}

/**
 * Get the first valid URL from photo_urls (handles Json type from Supabase)
 */
function extractFirstPhotoUrl(photoUrls: unknown): string | null {
  if (!photoUrls) return null;
  
  // Already an array
  if (Array.isArray(photoUrls)) {
    const first = photoUrls[0];
    return typeof first === 'string' && first.length > 0 ? first : null;
  }
  
  // Direct string URL
  if (typeof photoUrls === 'string') {
    if (photoUrls.startsWith('http')) {
      return photoUrls;
    }
    // Try parsing as JSON
    try {
      const parsed = JSON.parse(photoUrls);
      if (Array.isArray(parsed) && typeof parsed[0] === 'string') {
        return parsed[0];
      }
    } catch {
      // ignore
    }
  }
  
  return null;
}

/**
 * Get instant profile photo URL (non-hook version)
 * Priority: globalDataStore -> persistentCache -> null
 * Use this for getting photo URL outside React render or before DB query
 * V250: Never returns "__BLOB_CACHED__" sentinel - uses structured cache resolver
 */
export async function getInstantProfilePhotoUrl(userId: string): Promise<string | null> {
  // V250: Migrate any legacy per-user photo cache keys on first access
  try {
    migrateLegacyProfilePhotoCache(userId);
  } catch {
    // Non-critical; fall through to normal lookup
  }

  // Priority 1: globalDataStore (truth)
  const gs = globalDataStore.getProfile();
  const gsUrl = gs?.photo_urls?.[0];
  if (gsUrl && typeof gsUrl === 'string') {
    return gsUrl;
  }

  // Priority 2: persistentCache (survives reload) - use structured resolver
  try {
    const { resolveProfilePhotoCacheUrl } = await import('../lib/profilePhotoKeys');
    const resolvedUrl = await resolveProfilePhotoCacheUrl(userId);
    if (resolvedUrl) {
      return resolvedUrl;
    }
  } catch (error) {
    if (import.meta.env.DEV) {
      console.warn('⚠️ V250: Failed to resolve instant profile photo cache URL:', error);
    }
  }

  return null;
}

export function useProfilePhoto(
  userId: string | null | undefined,
  authPhotoUrl?: string | null
): UseProfilePhotoResult {
  const { getCachedPhotoUrl, updateUserPhoto, clearCache } = usePhotoContext();
  const currentUser = useCurrentUser();
  const otherUser = useUser(userId || '');
  const updateCurrentUser = useUpdateCurrentUser();
  
  const isCurrentUser = !!(userId && currentUser?.id === userId);
  
  // V244: PhotoStore state (stable key cache - single source of truth)
  const [photoStoreUrl, setPhotoStoreUrl] = useState<string | null>(null);
  
  // V244: Load from PhotoStore (stable key) on mount/userId change and when PhotoStore updates
  useEffect(() => {
    if (!userId) {
      setPhotoStoreUrl(null);
      return;
    }
    
    let cancelled = false;
    
    const loadPhoto = async () => {
      try {
        const url = await getPhoto(userId);
        if (!cancelled) {
          // V257: Log what PhotoStore returned (blob vs remote)
          const isBlob = url?.startsWith('blob:') ?? false;
          const isRemote = url?.startsWith('http') ?? false;
          console.log(`🔍 V257 useProfilePhoto: PhotoStore.getPhoto() returned:`, {
            userId,
            url: url ? url.substring(0, 80) : 'null',
            isBlob,
            isRemote,
            correct: isRemote && !isBlob,
          });
          if (isBlob) {
            // V258.2: Removed V257 warning - now using state-aware getPhoto
            // console.warn(`⚠️ V257 useProfilePhoto: PhotoStore returned blob URL (should be remote after upload success)`);
          }
          setPhotoStoreUrl(url);
        }
      } catch (error) {
        console.warn('⚠️ V244: PhotoStore get failed:', error);
        if (!cancelled) {
          setPhotoStoreUrl(null);
        }
      }
    };
    
    loadPhoto();
    
    // Subscribe to PhotoStore updates for this userId
    const unsubscribe = subscribeToPhotoUpdates((updatedUserId) => {
      if (updatedUserId === userId && !cancelled) {
        // Reload photo when PhotoStore updates
        loadPhoto();
      }
    });
    
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [userId]);

  // Compute the best photo URL with priority
  // V253: Use pickBestPhotoSrc helper to never return dead blob URLs
  const photoUrl = useMemo(() => {
    if (!userId) return null;
    
    // Get all potential sources
    const overrideUrl = getOverride(userId);
    const gs = globalDataStore.getProfile();
    const gsUrl = gs?.photo_urls?.[0];
    const cachedUrl = getCachedPhotoUrl(userId);
    
    // Get cache payload
    let cachePayload: { kind: 'blob' | 'url'; blobKey?: string; url?: string } | null = null;
    try {
      migrateLegacyProfilePhotoCache(userId);
      const payload = getProfilePhotoCachePayload(userId);
      if (payload) {
        cachePayload = payload;
      }
    } catch {
      // Non-critical
    }
    
    // Get remote URL from DB
    let remoteUrl: string | null = null;
    if (isCurrentUser && currentUser?.photo_urls) {
      remoteUrl = extractFirstPhotoUrl(currentUser.photo_urls);
    } else if (!isCurrentUser && otherUser?.photo_urls) {
      remoteUrl = extractFirstPhotoUrl(otherUser.photo_urls);
    }
    
    // V253: Use pickBestPhotoSrc to select source (never returns dead blob)
    // Priority: override > PhotoStore > globalDataStore > cachePayload > cachedUrl > remoteUrl
    const bestUrl = pickBestPhotoSrc({
      uiOverride: overrideUrl,
      photoStoreUrl, // Pass separately so it can verify blob
      cachePayload,
      remoteUrl: gsUrl || cachedUrl || remoteUrl, // Combine all remote URLs
      userId,
    });
    
    if (bestUrl) {
      // V320: Never return Google placeholder as "final" – must go through Cloudinary pipeline
      if (bestUrl.includes('googleusercontent.com') || bestUrl.includes('lh3.googleusercontent')) {
        if (import.meta.env.DEV) console.log('[V320] useProfilePhoto: Ignoring Google URL as final (use placeholder + bootstrap)');
        return null;
      }
      return bestUrl;
    }
    
    // V320: Do NOT return Google avatar as final – UI shows placeholder; ensure-user-row converts to Cloudinary
    if (isCurrentUser && authPhotoUrl && typeof authPhotoUrl === 'string') {
      if (authPhotoUrl.includes('googleusercontent.com') || authPhotoUrl.includes('lh3.googleusercontent')) {
        return null; // Trigger placeholder + optional bootstrap in Profile
      }
      return authPhotoUrl;
    }
    
    return null;
  }, [userId, photoStoreUrl, getCachedPhotoUrl, isCurrentUser, currentUser?.photo_urls, otherUser?.photo_urls, authPhotoUrl]);
  
  // Update photo in both contexts
  const updatePhoto = useCallback(async (newUrl: string) => {
    if (!userId) return;
    
    console.log('🔄 V240: Updating photo in unified hook');
    console.log('📋 V240: userId:', userId, 'isCurrentUser:', isCurrentUser);
    console.log('📋 V240: newUrl:', newUrl.substring(0, 80));
    
    // 1. Update PhotoContext immediately (L1 cache with cache-busted URL)
    updateUserPhoto(userId, newUrl, 'cloudinary');
    
    // 2. Update NetworkDataContext (DB cache)
    // V213.5: NEVER update DB with blob/data URLs - only Cloudinary URLs
    if (isCurrentUser && updateCurrentUser) {
      try {
        // V213.5: Defensive check - block blob/data URLs
        if (newUrl.startsWith('blob:') || newUrl.startsWith('data:')) {
          console.error('[P0_BLOCKED] useProfilePhoto: Attempt to update DB with blob/data URL:', newUrl.substring(0, 100));
          console.trace('[P0_BLOCKED_STACK] useProfilePhoto stack trace:');
          return; // Don't update DB with blob URLs
        }

        // Strip cache-buster for DB storage
        const cleanUrl = newUrl.split('?')[0];
        
        // V213.5: Additional check after stripping query params
        if (cleanUrl.startsWith('blob:') || cleanUrl.startsWith('data:')) {
          console.error('[P0_BLOCKED] useProfilePhoto: Clean URL is still blob/data:', cleanUrl.substring(0, 100));
          return;
        }

        await updateCurrentUser({ photo_urls: [cleanUrl] });
        console.log('✅ V240: NetworkDataContext updated with Cloudinary URL');
      } catch (error) {
        console.error('❌ V240: Failed to update NetworkDataContext:', error);
      }
    }
  }, [userId, isCurrentUser, updateUserPhoto, updateCurrentUser]);
  
  // Clear photo cache
  const clearPhoto = useCallback(() => {
    if (userId) {
      clearCache(userId);
    }
  }, [userId, clearCache]);
  
  return {
    photoUrl,
    updatePhoto,
    clearPhoto,
    isCurrentUser,
  };
}

/**
 * V240: Get photo URL for any user (non-hook version for callbacks)
 * Use this when you need to get a photo URL outside of React render
 */
export function getProfilePhotoUrl(
  userId: string,
  getCachedPhotoUrl: (id: string) => string | null,
  currentUser: { id: string; photo_urls?: unknown } | null,
  otherUser: { photo_urls?: unknown } | null
): string | null {
  // Priority 1: PhotoContext cache
  const cachedUrl = getCachedPhotoUrl(userId);
  if (cachedUrl) return cachedUrl;
  
  // Priority 2: Current user
  if (currentUser?.id === userId && currentUser.photo_urls) {
    return extractFirstPhotoUrl(currentUser.photo_urls);
  }
  
  // Priority 3: Other user
  if (otherUser?.photo_urls) {
    return extractFirstPhotoUrl(otherUser.photo_urls);
  }
  
  return null;
}
